import React, { useEffect, useId, useMemo, useState } from 'react';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { Select, Spin } from 'antd';
import styled from 'styled-components';

import { useLinkProfile } from '../api/useLinkProfile';
import { ClickStatsChartResponse } from '../models';

export type ClickStatsPeriod = 'week' | 'month' | 'year';

export interface ClickHourBucket {
  hour: number;
  label: string;
  today: number;
  compare: number;
}

export interface ClickMetric {
  label: string;
  value: string;
  changePercent: number;
  compareLabel?: string;
}

export interface ClickStatsData {
  title?: string;
  todayLabel: string;
  compareLabel: string;
  todayDateLabel: string;
  compareDateLabel: string;
  metrics: ClickMetric[];
  buckets: ClickHourBucket[];
  currentHour: number;
  bucketTooltipLabel?: (bucket: ClickHourBucket) => string;
}

interface ClickStatsProps {
  data?: ClickStatsData;
  period?: ClickStatsPeriod;
  onPeriodChange?: (period: ClickStatsPeriod) => void;
  storeId?: number | string | null;
}

const PERIOD_OPTIONS: { value: ClickStatsPeriod; label: string }[] = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];

const COLORS = {
  accent: '#2f6fed',
  accentSoft: 'rgba(47, 111, 237, 0.12)',
  up: '#1a7f37',
  down: '#cf222e',
  muted: '#6b7280',
  axis: '#9ca3af',
  grid: '#d1d5db',
  border: '#e5e7eb',
  text: '#111827',
};

const Card = styled.div`
  background: #fff;
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  padding: 20px 24px 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: ${COLORS.text};
  letter-spacing: -0.02em;
`;

const Divider = styled.div`
  height: 1px;
  background: ${COLORS.border};
  margin-bottom: 20px;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 240px) 1fr;
  gap: 28px;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Metrics = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding-top: 4px;
`;

const MetricLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.accent};
  margin-bottom: 4px;
`;

const MetricValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${COLORS.text};
  line-height: 1.15;
  letter-spacing: -0.02em;
`;

const MetricChange = styled.div<{ $positive: boolean }>`
  margin-top: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => (p.$positive ? COLORS.up : COLORS.down)};
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const CompareHint = styled.span`
  font-weight: 500;
  color: ${COLORS.muted};
`;

const ChartPanel = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Legend = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 18px;
  margin-bottom: 8px;
`;

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${COLORS.muted};
`;

const Dot = styled.span<{ $hatched?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) =>
    p.$hatched
      ? `repeating-linear-gradient(
          -45deg,
          ${COLORS.accent},
          ${COLORS.accent} 1.5px,
          #fff 1.5px,
          #fff 3.5px
        )`
      : COLORS.accent};
  border: 1px solid ${COLORS.accent};
  flex-shrink: 0;
`;

const ChartWrap = styled.div`
  position: relative;
  width: 100%;
  height: 240px;
`;

const Svg = styled.svg`
  width: 100%;
  height: 100%;
  display: block;
  overflow: visible;
`;

const TooltipBox = styled.div<{ $topPct: number; $leftPct: number }>`
  position: absolute;
  left: ${(p) => p.$leftPct}%;
  top: ${(p) => p.$topPct}%;
  transform: translate(-50%, -100%);
  background: #fff;
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  padding: 10px 12px;
  min-width: 168px;
  pointer-events: none;
  z-index: 2;
`;

const TooltipTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.text};
  margin-bottom: 8px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: ${COLORS.text};
  margin-top: 6px;
`;

const TooltipLeft = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.muted};
`;

const TooltipChange = styled.div<{ $positive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => (p.$positive ? COLORS.up : COLORS.down)};
  margin: 4px 0 2px 18px;
`;

function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(Math.round(value));
}

function formatCompactAxis(value: number): string {
  if (value === 0) return '0';
  if (value >= 1000) return `${Math.round(value / 1000)}k`;
  return String(Math.round(value));
}

function percentChange(today: number, compare: number): number {
  if (compare === 0) return today === 0 ? 0 : 100;
  return ((today - compare) / compare) * 100;
}

function shortDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function buildMetrics(
  buckets: ClickHourBucket[],
  avgLabel: string,
  compareHint: string,
  pagesHit = 101,
  comparePagesHit = 100
): ClickMetric[] {
  const totalClicks = buckets.reduce((sum, b) => sum + b.today, 0);
  const compareClicks = buckets.reduce((sum, b) => sum + b.compare, 0);
  const activeBuckets = Math.max(1, buckets.filter((b) => b.today > 0).length);
  const compareActive = Math.max(1, buckets.filter((b) => b.compare > 0).length);
  const avg = totalClicks / activeBuckets;
  const compareAvg = compareClicks / compareActive;

  return [
    {
      label: 'Total clicks',
      value: formatCount(totalClicks),
      changePercent: percentChange(totalClicks, compareClicks),
      compareLabel: compareHint,
    },
    {
      label: 'Pages hit',
      value: formatCount(pagesHit),
      changePercent: percentChange(pagesHit, comparePagesHit),
    },
    {
      label: avgLabel,
      value: formatCount(avg),
      changePercent: percentChange(avg, compareAvg),
    },
  ];
}

function buildWeekStats(now: Date): ClickStatsData {
  const dayCount = 7;
  const currentDay = (now.getDay() + 6) % 7; // Mon=0 … Sun=6
  const buckets: ClickHourBucket[] = [];

  for (let i = 0; i < dayCount; i++) {
    const date = new Date(now);
    date.setDate(now.getDate() - currentDay + i);
    const base = [18, 24, 32, 41, 55, 70, 48][i];
    const compareBase = [16, 22, 28, 38, 50, 64, 44][i];
    const jitter = (i * 11) % 7;
    buckets.push({
      hour: i,
      label: date.toLocaleDateString('en-US', { weekday: 'short' }),
      today: i > currentDay ? 0 : base + jitter,
      compare: compareBase + jitter,
    });
  }

  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - currentDay);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const prevWeekStart = new Date(weekStart);
  prevWeekStart.setDate(weekStart.getDate() - 7);
  const prevWeekEnd = new Date(weekEnd);
  prevWeekEnd.setDate(weekEnd.getDate() - 7);

  return {
    title: 'Clicks',
    todayLabel: 'This week',
    compareLabel: 'Last week',
    todayDateLabel: `${shortDate(weekStart)} – ${shortDate(weekEnd)}`,
    compareDateLabel: `${shortDate(prevWeekStart)} – ${shortDate(prevWeekEnd)}`,
    currentHour: currentDay,
    bucketTooltipLabel: (bucket) => bucket.label,
    metrics: buildMetrics(
      buckets,
      'Average per day',
      'compared to last week',
      64,
      58
    ),
    buckets,
  };
}

function buildMonthStats(now: Date): ClickStatsData {
  const dayCount = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const currentDay = now.getDate() - 1;
  const buckets: ClickHourBucket[] = [];

  for (let i = 0; i < dayCount; i++) {
    const wave = Math.round(20 + 25 * Math.sin((i / dayCount) * Math.PI * 2));
    const compareWave = Math.round(
      18 + 22 * Math.sin((i / dayCount) * Math.PI * 2 + 0.4)
    );
    const jitter = (i * 13) % 9;
    buckets.push({
      hour: i,
      label: String(i + 1),
      today: i > currentDay ? 0 : wave + jitter,
      compare: compareWave + jitter,
    });
  }

  const monthLabel = now.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevMonthLabel = prev.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  return {
    title: 'Clicks',
    todayLabel: 'This month',
    compareLabel: 'Last month',
    todayDateLabel: monthLabel,
    compareDateLabel: prevMonthLabel,
    currentHour: currentDay,
    bucketTooltipLabel: (bucket) => `Day ${bucket.label}`,
    metrics: buildMetrics(
      buckets,
      'Average per day',
      'compared to last month',
      210,
      188
    ),
    buckets,
  };
}

function buildYearStats(now: Date): ClickStatsData {
  const currentMonth = now.getMonth();
  const buckets: ClickHourBucket[] = [];
  const monthBases = [42, 48, 55, 62, 78, 95, 110, 102, 88, 70, 58, 50];
  const compareBases = [38, 44, 50, 58, 72, 90, 104, 96, 82, 66, 54, 46];

  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), i, 1);
    const jitter = (i * 7) % 11;
    buckets.push({
      hour: i,
      label: date.toLocaleDateString('en-US', { month: 'short' }),
      today: i > currentMonth ? 0 : monthBases[i] + jitter,
      compare: compareBases[i] + jitter,
    });
  }

  return {
    title: 'Clicks',
    todayLabel: 'This year',
    compareLabel: 'Last year',
    todayDateLabel: String(now.getFullYear()),
    compareDateLabel: String(now.getFullYear() - 1),
    currentHour: currentMonth,
    bucketTooltipLabel: (bucket) => bucket.label,
    metrics: buildMetrics(
      buckets,
      'Average per month',
      'compared to last year',
      980,
      910
    ),
    buckets,
  };
}

/** Demo click data for Starter+ analytics, keyed by period. */
export function buildDemoClickStats(
  period: ClickStatsPeriod = 'week',
  now = new Date()
): ClickStatsData {
  if (period === 'month') return buildMonthStats(now);
  if (period === 'year') return buildYearStats(now);
  return buildWeekStats(now);
}

export function mapChartResponseToClickStats(
  chart: ClickStatsChartResponse
): ClickStatsData {
  return {
    title: chart.title || 'Clicks',
    todayLabel: chart.current_label,
    compareLabel: chart.compare_label,
    todayDateLabel: chart.current_date_label,
    compareDateLabel: chart.compare_date_label,
    currentHour: chart.current_index,
    buckets: chart.buckets.map((bucket) => ({
      hour: bucket.index,
      label: bucket.label,
      today: bucket.current,
      compare: bucket.compare,
    })),
    metrics: chart.metrics.map((metric) => ({
      label: metric.label,
      value: metric.value,
      changePercent: metric.change_percent,
      compareLabel: metric.compare_label,
    })),
    bucketTooltipLabel: (bucket) =>
      chart.period === 'month' ? `Day ${bucket.label}` : bucket.label,
  };
}

const CHART = {
  padL: 44,
  padR: 28,
  padT: 12,
  padB: 28,
  barW: 7,
  gap: 2,
};

export const ClickStats: React.FC<ClickStatsProps> = ({
  data,
  period: controlledPeriod,
  onPeriodChange,
  storeId = null,
}) => {
  const [internalPeriod, setInternalPeriod] =
    useState<ClickStatsPeriod>('week');
  const period = controlledPeriod ?? internalPeriod;
  const { linkProfileStatsChart } = useLinkProfile();
  const [remoteStats, setRemoteStats] = useState<ClickStatsData | null>(null);
  const [loading, setLoading] = useState(!data);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setRemoteStats(null);
      setLoading(false);
      setLoadError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    linkProfileStatsChart(period, storeId)
      .then((chart) => {
        if (!cancelled) {
          setRemoteStats(mapChartResponseToClickStats(chart));
        }
      })
      .catch((err) => {
        console.error('Failed to load click stats chart', err);
        if (!cancelled) {
          setRemoteStats(null);
          setLoadError('Failed to load click stats.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [data, period, storeId, linkProfileStatsChart]);

  const stats = data ?? remoteStats;
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const hatchId = useId().replace(/:/g, '');

  const bucketCount = Math.max(stats?.buckets.length ?? 1, 1);

  const maxValue = useMemo(() => {
    if (!stats) return 50;
    const peak = Math.max(
      ...stats.buckets.flatMap((b) => [b.today, b.compare]),
      1
    );
    const step = peak > 300 ? 100 : 50;
    return Math.max(step, Math.ceil(peak / step) * step);
  }, [stats]);

  const yTicks = useMemo(() => {
    const steps = 3;
    return Array.from({ length: steps + 1 }, (_, i) => (maxValue / steps) * i);
  }, [maxValue]);

  const activeIndex = hoverIndex;
  const activeBucket =
    activeIndex === null || !stats
      ? null
      : stats.buckets.find((b) => b.hour === activeIndex) ?? null;

  const viewW = 640;
  const viewH = 240;
  const plotW = viewW - CHART.padL - CHART.padR;
  const plotH = viewH - CHART.padT - CHART.padB;

  const xForIndex = (index: number) =>
    CHART.padL + ((index + 0.5) / bucketCount) * plotW;

  const yForValue = (value: number) =>
    CHART.padT + plotH - (value / maxValue) * plotH;

  const slotWidth = plotW / bucketCount;
  // Month has ~28–31 days: keep bars slim so neighboring days stay visually separated.
  const barWidth =
    period === 'month'
      ? Math.max(2, Math.min(3.5, slotWidth * 0.18))
      : Math.min(CHART.barW, Math.max(3, (slotWidth - CHART.gap) / 2));
  const pairGap = period === 'month' ? Math.max(1, slotWidth * 0.08) : CHART.gap;
  const groupHalfWidth = barWidth + pairGap / 2 + 2;

  const tickIndexes = useMemo(() => {
    if (!stats) return [];
    if (bucketCount <= 8) {
      return stats.buckets.map((b) => b.hour);
    }
    if (bucketCount <= 24) {
      return stats.buckets
        .filter((_, i) => i % 3 === 0)
        .map((b) => b.hour);
    }
    const step = Math.ceil(bucketCount / 6);
    return stats.buckets
      .filter((_, i) => i % step === 0)
      .map((b) => b.hour);
  }, [stats, bucketCount]);

  const handlePeriodChange = (next: ClickStatsPeriod) => {
    if (controlledPeriod === undefined) {
      setInternalPeriod(next);
    }
    onPeriodChange?.(next);
    setHoverIndex(null);
  };

  const tooltipTitle = activeBucket
    ? stats?.bucketTooltipLabel?.(activeBucket) ?? activeBucket.label
    : '';

  return (
    <Card>
      <TitleRow>
        <Title>{stats?.title ?? 'Clicks'}</Title>
        <Select
          value={period}
          options={PERIOD_OPTIONS}
          onChange={handlePeriodChange}
          style={{ width: 120 }}
          aria-label="Clicks period"
        />
      </TitleRow>
      <Divider />
      {loading ? (
        <div className="flex justify-center py-16">
          <Spin size="large" />
        </div>
      ) : loadError || !stats ? (
        <div className="py-12 text-center text-sm text-gray-500">
          {loadError || 'No click stats available.'}
        </div>
      ) : (
      <Layout>
        <Metrics>
          {stats.metrics.map((metric) => {
            const positive = metric.changePercent >= 0;
            return (
              <div key={metric.label}>
                <MetricLabel>{metric.label}</MetricLabel>
                <MetricValue>{metric.value}</MetricValue>
                <MetricChange $positive={positive}>
                  {positive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                  {Math.abs(metric.changePercent).toFixed(2)}%
                  {metric.compareLabel ? (
                    <CompareHint>{metric.compareLabel}</CompareHint>
                  ) : null}
                </MetricChange>
              </div>
            );
          })}
        </Metrics>

        <ChartPanel>
          <Legend>
            <LegendItem>
              <Dot />
              {stats.todayLabel}
            </LegendItem>
            <LegendItem>
              <Dot $hatched />
              {stats.compareLabel}
            </LegendItem>
          </Legend>

          <ChartWrap onMouseLeave={() => setHoverIndex(null)}>
            <Svg
              viewBox={`0 0 ${viewW} ${viewH}`}
              role="img"
              aria-label={`Clicks by ${period}`}
            >
              <defs>
                <pattern
                  id={hatchId}
                  patternUnits="userSpaceOnUse"
                  width="6"
                  height="6"
                  patternTransform="rotate(-45)"
                >
                  <rect width="6" height="6" fill="#fff" />
                  <line
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="6"
                    stroke={COLORS.accent}
                    strokeWidth="2.2"
                  />
                </pattern>
              </defs>

              {yTicks.map((tick) => {
                const y = yForValue(tick);
                return (
                  <g key={tick}>
                    <line
                      x1={CHART.padL}
                      x2={viewW - CHART.padR}
                      y1={y}
                      y2={y}
                      stroke={COLORS.grid}
                      strokeDasharray="3 4"
                      strokeWidth={1}
                    />
                    <text
                      x={CHART.padL - 8}
                      y={y + 4}
                      textAnchor="end"
                      fill={COLORS.axis}
                      fontSize={11}
                    >
                      {formatCompactAxis(tick)}
                    </text>
                  </g>
                );
              })}

              {activeIndex !== null && (
                <rect
                  x={xForIndex(activeIndex) - groupHalfWidth}
                  y={CHART.padT}
                  width={groupHalfWidth * 2}
                  height={plotH}
                  fill={COLORS.accentSoft}
                  rx={2}
                />
              )}

              {stats.buckets.map((bucket) => {
                const cx = xForIndex(bucket.hour);
                const todayH = (bucket.today / maxValue) * plotH;
                const compareH = (bucket.compare / maxValue) * plotH;
                const todayY = CHART.padT + plotH - todayH;
                const compareY = CHART.padT + plotH - compareH;
                return (
                  <g
                    key={bucket.hour}
                    onMouseEnter={() => setHoverIndex(bucket.hour)}
                    style={{ cursor: 'pointer' }}
                  >
                    <rect
                      x={cx - barWidth - pairGap / 2}
                      y={todayY}
                      width={barWidth}
                      height={Math.max(todayH, 0)}
                      fill={COLORS.accent}
                      rx={1}
                    />
                    <rect
                      x={cx + pairGap / 2}
                      y={compareY}
                      width={barWidth}
                      height={Math.max(compareH, 0)}
                      fill={`url(#${hatchId})`}
                      stroke={COLORS.accent}
                      strokeWidth={1}
                      rx={1}
                    />
                    <rect
                      x={cx - groupHalfWidth}
                      y={CHART.padT}
                      width={groupHalfWidth * 2}
                      height={plotH}
                      fill="transparent"
                    />
                  </g>
                );
              })}

              <line
                x1={xForIndex(stats.currentHour)}
                x2={xForIndex(stats.currentHour)}
                y1={CHART.padT}
                y2={CHART.padT + plotH}
                stroke={COLORS.axis}
                strokeWidth={1}
              />
              <text
                x={xForIndex(stats.currentHour)}
                y={CHART.padT - 2}
                textAnchor="middle"
                fill={COLORS.muted}
                fontSize={11}
                fontWeight={600}
              >
                Now
              </text>

              {tickIndexes.map((index) => {
                const bucket = stats.buckets.find((b) => b.hour === index);
                return (
                  <text
                    key={index}
                    x={xForIndex(index)}
                    y={viewH - 6}
                    textAnchor="middle"
                    fill={COLORS.axis}
                    fontSize={11}
                  >
                    {bucket?.label ?? ''}
                  </text>
                );
              })}
            </Svg>

            {activeBucket && (
              <TooltipBox
                $leftPct={(xForIndex(activeBucket.hour) / viewW) * 100}
                $topPct={
                  (Math.max(
                    28,
                    yForValue(
                      Math.max(activeBucket.today, activeBucket.compare)
                    ) - 12
                  ) /
                    viewH) *
                  100
                }
              >
                <TooltipTitle>{tooltipTitle}</TooltipTitle>
                <TooltipRow>
                  <TooltipLeft>
                    <Dot />
                    {stats.todayDateLabel}
                  </TooltipLeft>
                  <strong>{formatCount(activeBucket.today)}</strong>
                </TooltipRow>
                <TooltipChange
                  $positive={
                    percentChange(activeBucket.today, activeBucket.compare) >= 0
                  }
                >
                  {percentChange(activeBucket.today, activeBucket.compare) >=
                  0 ? (
                    <ArrowUpOutlined />
                  ) : (
                    <ArrowDownOutlined />
                  )}
                  {Math.abs(
                    percentChange(activeBucket.today, activeBucket.compare)
                  ).toFixed(1)}
                  %
                </TooltipChange>
                <TooltipRow>
                  <TooltipLeft>
                    <Dot $hatched />
                    {stats.compareDateLabel}
                  </TooltipLeft>
                  <strong>{formatCount(activeBucket.compare)}</strong>
                </TooltipRow>
              </TooltipBox>
            )}
          </ChartWrap>
        </ChartPanel>
      </Layout>
      )}
    </Card>
  );
};

export default ClickStats;
